import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { StreamSet, TrackRow } from "../api/types";
import { playError, registerAudio, stopEmbed, unlock } from "./player";
import { useRights } from "../rights";

/**
 * The browse list with the releases opened out: one row per track.
 *
 * A row plays where somebody serves audio for it: Bandcamp, or -- for a
 * release that is not on Bandcamp -- the shop whose clip the row is (`via`).
 * The stream URLs are signed and short-lived, so they are not fetched with
 * the list -- a page of fifty rows would mint fifty URLs and use one. They
 * are fetched for the release when a row is clicked, and cached for as long
 * as the list is on screen, which makes playing a second track from the same
 * record free.
 */
export function TrackList({
  rows, loading, onOpenRelease, onSpeedTest, onAddToPlaylist, onAddToTestList,
}: {
  rows: TrackRow[];
  loading: boolean;
  onOpenRelease: (key: string) => void;
  /** The release, and the track within it the menu was opened on. */
  onSpeedTest: (key: string, pos: number) => void;
  /** This one track onto a playlist; the picker opens where the menu was. */
  onAddToPlaylist: (t: TrackRow, at: { x: number; y: number }) => void;
  /** This one track onto the test list: one click, no picker. */
  onAddToTestList: (t: TrackRow) => void;
}) {
  // The actions that do not fit on a row live in a menu: a list this dense
  // cannot carry a button per action per row, and hiding them behind a menu is
  // better than dropping them. A "⋯" button on the row opens it -- a phone has
  // no right-click, and iOS never fires contextmenu at all -- and a right-click
  // opens the same menu where there is a mouse.
  const [menu, setMenu] = useState<{ x: number; y: number; t: TrackRow } | null>(null);
  const rights = useRights();

  const menuBox = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    // A click INSIDE the menu must not dismiss it. This listener is on capture
    // so it beats anything else on the page -- which means it also beats the
    // menu's own buttons: it would set menu to null, React would unmount the
    // button, and its onClick would never run. That is exactly what stopped
    // "Speed test" working. Checking the target first is what makes a
    // capture-phase dismiss safe.
    const close = (e: Event) => {
      if (menuBox.current?.contains(e.target as Node)) return;
      setMenu(null);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setMenu(null);
    window.addEventListener("click", close, true);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("click", close, true);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", esc);
    };
  }, [menu]);
  // release key -> (track position -> stream URL)
  const urls = useRef<Map<string, Map<number, string>>>(new Map());
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<Record<string, string>>({});

  // Starting a Bandcamp embed elsewhere should silence this, and vice versa.
  useEffect(() => registerAudio(() => {
    audio.current?.pause();
    setPlaying(null);
  }), []);

  useEffect(() => () => { audio.current?.pause(); }, []);

  const id = (t: TrackRow) => `${t.key}#${t.pos}`;

  async function toggle(t: TrackRow) {
    if (!t.streamable) return;
    const me = id(t);

    if (playing === me) {
      audio.current?.pause();
      setPlaying(null);
      return;
    }
    setBusy(me);
    setFailed((f) => { const { [me]: _drop, ...rest } = f; return rest; });
    if (!audio.current) audio.current = new Audio();
    try {
      let forRelease = urls.current.get(t.key);
      if (!forRelease) {
        // The tap is what lets iOS play, and it does not survive the fetch:
        // touch the element now, while the tap is still the reason.
        unlock(audio.current);
        const set = await api.streams(t.key);
        forRelease = new Map(set.tracks.map((s) => [s.pos, s.url]));
        // A shop's clips against Bandcamp's rows do not share positions; a
        // shop's clips against its own rows (`via`) do, because the rows
        // were made from them.
        if (set.source && !t.via) forRelease = shopURLs(set, rows.filter((x) => x.key === t.key));
        urls.current.set(t.key, forRelease);
      }
      const url = forRelease.get(t.pos);
      if (!url) {
        setFailed((f) => ({ ...f, [me]: "nobody is streaming this track" }));
        return;
      }
      stopEmbed();
      audio.current.src = url;
      audio.current.onended = () => setPlaying(null);
      await audio.current.play();
      setPlaying(me);
    } catch (e) {
      setFailed((f) => ({ ...f, [me]: playError(e) }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
    {menu && (
      <div className="ctx" ref={menuBox} style={{
        // Kept on screen: a tap near the right edge opens it leftwards.
        left: Math.max(8, Math.min(menu.x, window.innerWidth - 176)),
        top: Math.max(8, Math.min(menu.y, window.innerHeight - 200)),
      }}>
        <button onClick={() => { onSpeedTest(menu.t.key, menu.t.pos); setMenu(null); }}>
          Speed test
        </button>
        <button onClick={() => { onOpenRelease(menu.t.key); setMenu(null); }}>
          Tracklist
        </button>
        {rights.has("playlists") && (
          <button onClick={() => { onAddToPlaylist(menu.t, { x: menu.x, y: menu.y }); setMenu(null); }}>
            Add to playlist…
          </button>
        )}
        {/* Only a track somebody streams can go on the decks; a row nobody
            serves audio for is not offered rather than failing later. */}
        {rights.has("decks") && menu.t.streamable && (
          <button onClick={() => { onAddToTestList(menu.t); setMenu(null); }}>
            Add to Beat match
          </button>
        )}
        {menu.t.bcUrl && (
          <a href={menu.t.bcUrl} target="_blank" rel="noopener noreferrer"
             onClick={() => setMenu(null)}>
            Open on Bandcamp
          </a>
        )}
      </div>
    )}
    <table className="tracklist">
      <tbody>
        {rows.map((t) => {
          const me = id(t);
          const isPlaying = playing === me;
          return (
            <tr key={me} className={isPlaying ? "playing" : ""}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY, t });
                }}>
              <td className="play">
                <button
                  className={`p${isPlaying ? " on" : ""}`}
                  disabled={!t.streamable || busy === me}
                  onClick={() => void toggle(t)}
                  title={
                    t.streamable
                      ? isPlaying ? "pause" : t.via ? `play ${t.via}'s clip` : "play this track"
                      : "nobody serves audio for this track"
                  }
                >
                  {busy === me ? "…" : isPlaying ? "❚❚" : "▶"}
                </button>
              </td>
              <td className="art">
                {t.art && (t.bcUrl ? (
                  <a href={t.bcUrl} target="_blank" rel="noopener noreferrer"
                     title="open this release on Bandcamp">
                    <img src={t.art} alt="" loading="lazy" width={40} height={40} />
                  </a>
                ) : (
                  <img src={t.art} alt="" loading="lazy" width={40} height={40} />
                ))}
              </td>
              <td className="t">
                <span className="tt">{t.trackLabel && <em className="lbl">{t.trackLabel} </em>}{t.title}</span>
                <span className="ta">{t.artist}{t.via && <span className="via"> · clip from {t.via}</span>}</span>
                {/* All four kinds, in the colours the cards use, because a tag
                    means a different thing depending on who wrote it. They
                    describe the release rather than the cut, which is why they
                    repeat down its rows. */}
                <span className="tg">
                  {t.siteTags.map((x) => <em key={`s${x}`} className="g">{x}</em>)}
                  {t.bcTags.map((x) => <em key={`b${x}`} className="bc">{x}</em>)}
                  {t.discogsTags.map((x) => <em key={`d${x}`} className="dc">{x}</em>)}
                </span>
                {failed[me] && <span className="err">{failed[me]}</span>}
              </td>
              <td className="al">
                {/* The release this came from, still one click away: opening
                    the record is the thing a track row most often leads to. */}
                <button className="link" onClick={() => onOpenRelease(t.key)} title={t.album}>
                  {t.album}
                </button>
                {t.label && <span className="lb">{t.label}</span>}
              </td>
              <td className="d">{t.dur ? fmt(t.dur) : ""}</td>
              <td className={`b${t.bpm.state === "measured" ? "" : " none"}`}
                  title={t.bpm.conf ? `confidence ${t.bpm.conf}` : undefined}>
                {t.bpm.state === "measured" ? `${t.bpm.value}` : "—"}
              </td>
              <td className={`k${t.key_ ? "" : " none"}`}
                  title={t.keyConf ? `confidence ${t.keyConf}` : "not read yet"}>
                {t.key_ ?? "—"}
              </td>
              <td className={`m${t.meter ? "" : " none"}`}
                  title={t.meterConf ? `confidence ${t.meterConf}` : "not read yet"}>
                {t.meter ?? "—"}
              </td>
              <td className="acts">
                <button className="p" aria-label="more actions" title="speed test, tracklist, playlist, beat match…"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          setMenu({ x: r.right - 170, y: r.bottom + 4, t });
                        }}>⋯</button>
              </td>
            </tr>
          );
        })}
        {!rows.length && !loading && (
          <tr><td colSpan={9} className="note">Nothing matches these filters.</td></tr>
        )}
      </tbody>
    </table>
    </>
  );
}

function fmt(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * A shop's clips are numbered by the shop, not by Bandcamp, so their positions
 * cannot be trusted against these rows. Pair by title where the names agree,
 * and by order where they do not -- the shop lists the same record in the
 * same order more often than not.
 */
function shopURLs(set: StreamSet, tracks: TrackRow[]): Map<number, string> {
  const clips = set.tracks.filter((s) => s.kind !== "embed");
  const norm = (x: string) => x.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
  const out = new Map<number, string>();
  const used = new Set<number>();
  for (const t of tracks) {
    const want = norm(t.title);
    const i = clips.findIndex((c, j) => !used.has(j) && want && (norm(c.title) === want
      || norm(c.title).includes(want) || want.includes(norm(c.title))));
    const hit = i >= 0 ? clips[i] : undefined;
    if (hit) { used.add(i); out.set(t.pos, hit.url); }
  }
  for (const t of tracks) {
    if (out.has(t.pos)) continue;
    const j = t.pos - 1;
    const next = !used.has(j) ? clips[j] : undefined;
    if (next) { used.add(j); out.set(t.pos, next.url); }
  }
  return out;
}
